import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Card, CircularProgress, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import apiService from 'Network/apiService';
import { API_ENDPOINTS } from 'Network/apiEndpoints';
import { usePageTitle } from 'contexts/PageTitleContext';
import { BulkOrderDetailsView, formatBulkProductData } from './bulkProductList';

const matchesRouteOrderId = (item, routeOrderId) => {
  if (!item || !routeOrderId) return false;
  return (
    String(item.id) === String(routeOrderId) ||
    String(item.order_id) === String(routeOrderId) ||
    String(item.order?.order_id) === String(routeOrderId) ||
    String(item.order?.id) === String(routeOrderId)
  );
};

const BulkOrderDetails = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPageTitle('Bulk Order');
  }, [setPageTitle]);

  const fetchProduct = useCallback(async () => {
    if (!orderId) {
      setProduct(null);
      setLoading(false);
      return;
    }

    const decodedOrderId = decodeURIComponent(orderId);
    const stateProduct = location.state?.product;
    const statusFilter = location.state?.statusFilter;

    if (stateProduct && matchesRouteOrderId(stateProduct, decodedOrderId)) {
      setProduct(stateProduct);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const userId = localStorage.getItem('user_id');
      const statusParam = statusFilter ? `&status=${encodeURIComponent(statusFilter)}` : '';
      const url = `${API_ENDPOINTS.GET_bulk_upload_product}?user_id=${userId}${statusParam}&page=1&page_size=50&search=${encodeURIComponent(decodedOrderId)}`;
      const response = await apiService.get(url);
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.results)
          ? response.results
          : [];
      const found = list.find((item) => matchesRouteOrderId(item, decodedOrderId)) || null;
      setProduct(found ? formatBulkProductData(found) : null);
    } catch (error) {
      console.error('Error fetching bulk order details:', error);
      setProduct(null);
    } finally {
      setLoading(false);
    }
  }, [orderId, location.state]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/dashboard/bulk-upload')}
        sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
      >
        Back to Bulk Orders
      </Button>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={40} />
        </Box>
      ) : product ? (
        <Card sx={{ overflow: 'visible' }}>
          <BulkOrderDetailsView product={product} />
        </Card>
      ) : (
        <Typography color="text.secondary">Order not found.</Typography>
      )}
    </Box>
  );
};

export default BulkOrderDetails;
